/* eslint-disable jsx-a11y/anchor-is-valid */
import {process, State} from "@progress/kendo-data-query"
import {Button} from "@progress/kendo-react-buttons"
import {ExcelExport} from "@progress/kendo-react-excel-export"
import {Grid, GridCellProps, GridColumn, GridNoRecords, GridToolbar} from "@progress/kendo-react-grid"
import {Tooltip} from "@progress/kendo-react-tooltip"
import PropTypes from "prop-types"
import {useEffect, useRef, useState} from "react"
import {
    Customer,
    customerInit,
    fetchCustomerContact,
    fetchCustomerDetail,
    setCustomerContact,
    setCustomerDetail,
    setSelectedCustomer,
    toggleAddEdit,
} from "../../../containers/Customer/customerSlice"
import {RootState} from "../../../rootReducer"
import {SpinnerGrid} from "../../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../../store"
import {columnProps} from "../../../shared/ActiveGridFilter"
import {parseXLStrValToNum, processExportData} from "../../../utils/common"
import {PermissionsChecker} from "../../Shared/permissionChecker"

const CustomerGrid = () => {
    const dispatch = useAppDispatch()
    const customers = useAppSelector((state: RootState) => state.containers.customer.customers)
    const loading = useAppSelector((state: RootState) => state.containers.customer.loading)
    const initialState: State = {skip: 0, take: 100}
    const _export = useRef<ExcelExport | null>(null)
    const [dataState, setDataState] = useState<State>(initialState)

    useEffect(() => {
        setDataState(initialState)
    }, [customers])

    const toggleEditUserModal = (e: any, props: any) => {
        e.preventDefault()
        dispatch(toggleAddEdit(true))
        dispatch(setSelectedCustomer(props.dataItem))
        dispatch(fetchCustomerDetail(props.dataItem.customerId))
        dispatch(fetchCustomerContact(props.dataItem.customerId))
    }
    const AddLinkCell = (props: any) => (
        <td className="k-grid-content-sticky" style={{left: "0px"}}>
            <a
                style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                }}
                onClick={event => toggleEditUserModal(event, props)}
            >
                {props.dataItem.accountNo}
            </a>
        </td>
    )

    const CustomColumnCell = (props: GridCellProps) => (
        <td title={props.dataItem[`${props.field}`]}>{props.dataItem[`${props.field}`]}</td>
    )

    const exportExcelData = (customersData: Customer[]) =>
        customersData.map(({accountNo, ...item}) => ({
            accountNo: parseXLStrValToNum(accountNo),
            ...item,
        }))

    return (
        <div className="position-relative">
            {loading && <SpinnerGrid />}
            <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                <ExcelExport
                    fileName={crypto.randomUUID()}
                    data={exportExcelData(processExportData(customers, dataState))}
                    ref={_export}
                >
                    <Grid
                        style={{
                            height: "calc(100vh - 14.3rem)",
                        }}
                        data={process(customers, dataState)}
                        {...dataState}
                        onDataStateChange={event => setDataState(event.dataState)}
                        pageable={true}
                        sortable={true}
                    >
                        <GridNoRecords>&nbsp;</GridNoRecords>
                        <GridToolbar>
                            <PermissionsChecker permission={["Customers.Add"]}>
                                <Button
                                    fillMode="flat"
                                    themeColor={"primary"}
                                    className="buttons-container-button"
                                    iconClass="fa fa-plus"
                                    onClick={() => {
                                        dispatch(toggleAddEdit(true))
                                        dispatch(setCustomerDetail(customerInit))
                                        dispatch(setCustomerContact([]))
                                    }}
                                >
                                    New Customer
                                </Button>
                            </PermissionsChecker>
                            <Button
                                fillMode="flat"
                                themeColor={"primary"}
                                className="buttons-container-button"
                                iconClass="fa fa-file-excel"
                                disabled={process(customers, dataState).total === 0}
                                onClick={() => _export.current !== null && _export.current.save()}
                            >
                                Export to Excel
                            </Button>
                        </GridToolbar>
                        <GridColumn
                            {...columnProps("accountNo", dataState)}
                            width="150px"
                            field="accountNo"
                            title="Account No"
                            locked
                            cell={props => <AddLinkCell {...props} />}
                        />
                        <GridColumn
                            {...columnProps("companyName", dataState)}
                            width="250px"
                            field="companyName"
                            title="Company Name"
                            locked
                        />
                        <GridColumn
                            {...columnProps("address1", dataState)}
                            width="200px"
                            cell={CustomColumnCell}
                            field="address1"
                            title="Address"
                        />
                        <GridColumn
                            {...columnProps("address2", dataState)}
                            width="200px"
                            cell={CustomColumnCell}
                            field="address2"
                            title="Address2"
                        />
                        <GridColumn
                            {...columnProps("address3", dataState)}
                            width="200px"
                            cell={CustomColumnCell}
                            field="address3"
                            title="Address3"
                        />
                        <GridColumn {...columnProps("city", dataState)} width="200px" field="city" title="City" />
                        <GridColumn
                            {...columnProps("postCode", dataState)}
                            width="200px"
                            field="postCode"
                            title="PostCode"
                        />
                        <GridColumn
                            {...columnProps("telephone1", dataState)}
                            width="200px"
                            field="telephone1"
                            title="Phone"
                        />
                        <GridColumn
                            {...columnProps("telephone2", dataState)}
                            width="200px"
                            field="telephone2"
                            title="Phone2"
                        />
                        <GridColumn {...columnProps("email", dataState)} width="200px" field="email" title="E-Mail" />
                        <GridColumn {...columnProps("web", dataState)} width="200px" field="web" title="Website" />
                    </Grid>
                </ExcelExport>
            </Tooltip>
        </div>
    )
}

CustomerGrid.propTypes = {
    customers: PropTypes.array,
    page: PropTypes.object,
    setPage: PropTypes.func,
    toggleShow: PropTypes.func,
    excelExport: PropTypes.func,
    filter: PropTypes.object,
    setFilter: PropTypes.func,
}
export default CustomerGrid
